// src/services/PublicServiceImportService.js
//
// Handles import and ongoing sync of public-service podcasts (DR and similar
// state-funded broadcasters). These podcasts are freely catalogued and do NOT
// participate in the creator payment calculation.
//
// Firestore fields added to podcast documents:
//   contentType: 'public_service'          — excludes from payment calc
//   publicServiceProvider: 'dr' | string   — which broadcaster
//   feedUrl: string                         — RSS feed URL (same field as creator imports)
//   lastSyncedAt: Timestamp                 — when episodes were last refreshed

import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  query,
  where,
  limit,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';

const RSS2JSON = 'https://api.rss2json.com/v1/api.json?rss_url=';

// ─── RSS fetch via rss2json proxy (handles CORS) ─────────────────────────────

async function fetchFeed(rssUrl) {
  const res = await fetch(`${RSS2JSON}${encodeURIComponent(rssUrl)}&count=200`);
  const data = await res.json();
  if (data.status !== 'ok') {
    throw new Error(`RSS parse error: ${data.message || 'unknown'}`);
  }
  return data;
}

// ─── Check if a podcast already exists by feedUrl ────────────────────────────

async function findExistingPodcast(feedUrl) {
  const q = query(
    collection(db, 'podcasts'),
    where('feedUrl', '==', feedUrl),
    limit(1)
  );
  const snap = await getDocs(q);
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
}

// ─── Import a single public-service podcast from its RSS URL ─────────────────

export async function importPublicServicePodcast(feedUrl, provider = 'dr', iTunesCategory = '') {
  try {
    if (!feedUrl) throw new Error('Feed URL is required');

    const existing = await findExistingPodcast(feedUrl);
    if (existing) {
      // Already imported — run a sync instead
      return await syncPublicServicePodcast(existing.id, feedUrl);
    }

    const data = await fetchFeed(feedUrl);
    const feed = data.feed;
    const items = data.items || [];

    const podcastData = {
      title: feed.title || 'Ukendt titel',
      description: feed.description || '',
      author: feed.author || provider.toUpperCase(),
      image: feed.image || '',
      feedUrl,
      link: feed.link || '',
      contentType: 'public_service',
      publicServiceProvider: provider,
      status: 'approved',
      createdAt: serverTimestamp(),
      dateImported: serverTimestamp(),
      lastSyncedAt: serverTimestamp(),
      episodeCount: items.length,
      category: iTunesCategory || feed.category || '',
      categoryId: '',
      isVerified: true,
    };

    const podcastRef = await addDoc(collection(db, 'podcasts'), podcastData);
    const podcastId = podcastRef.id;

    await _writeEpisodes(podcastId, items, feed.image);

    return {
      success: true,
      podcastId,
      isNew: true,
      message: `Importerede "${feed.title}" med ${items.length} episoder`,
      podcast: { title: feed.title, image: feed.image, episodeCount: items.length },
    };
  } catch (err) {
    console.error('PublicServiceImportService.importPublicServicePodcast:', err);
    return { success: false, message: err.message };
  }
}

// ─── Sync episodes for an existing public-service podcast ────────────────────
// Fetches the feed, diffs against stored episode GUIDs, inserts only new ones.

export async function syncPublicServicePodcast(podcastId, feedUrl) {
  try {
    const data = await fetchFeed(feedUrl);
    const items = data.items || [];

    // Load existing episode GUIDs so we don't re-insert
    const episodesSnap = await getDocs(
      collection(db, `podcasts/${podcastId}/episodes`)
    );
    const existingGuids = new Set(
      episodesSnap.docs.map(d => d.data().guid).filter(Boolean)
    );

    const newItems = items.filter(
      item => item.guid && !existingGuids.has(item.guid)
    );

    if (newItems.length > 0) {
      await _writeEpisodes(podcastId, newItems, data.feed.image);
    }

    await updateDoc(doc(db, 'podcasts', podcastId), {
      lastSyncedAt: serverTimestamp(),
      episodeCount: episodesSnap.size + newItems.length,
    });

    return {
      success: true,
      podcastId,
      isNew: false,
      newEpisodes: newItems.length,
      message: newItems.length > 0
        ? `Synkroniserede ${newItems.length} nye episoder`
        : 'Ingen nye episoder',
    };
  } catch (err) {
    console.error('PublicServiceImportService.syncPublicServicePodcast:', err);
    return { success: false, message: err.message };
  }
}

// ─── Sync all public-service podcasts in Firestore ───────────────────────────

export async function syncAllPublicServicePodcasts() {
  try {
    const q = query(
      collection(db, 'podcasts'),
      where('contentType', '==', 'public_service')
    );
    const snap = await getDocs(q);

    if (snap.empty) {
      return { success: true, total: 0, results: [] };
    }

    const results = await Promise.allSettled(
      snap.docs.map(d => syncPublicServicePodcast(d.id, d.data().feedUrl))
    );

    const resolved = results.map((r, i) =>
      r.status === 'fulfilled'
        ? r.value
        : { success: false, podcastId: snap.docs[i].id, message: r.reason?.message }
    );

    return {
      success: true,
      total: snap.size,
      synced: resolved.filter(r => r.success).length,
      newEpisodes: resolved.reduce((sum, r) => sum + (r.newEpisodes || 0), 0),
      results: resolved,
    };
  } catch (err) {
    console.error('PublicServiceImportService.syncAllPublicServicePodcasts:', err);
    return { success: false, message: err.message };
  }
}

// ─── Fetch all public-service podcasts from Firestore ────────────────────────

export async function getPublicServicePodcasts() {
  try {
    const q = query(
      collection(db, 'podcasts'),
      where('contentType', '==', 'public_service')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('PublicServiceImportService.getPublicServicePodcasts:', err);
    throw err;
  }
}

// ─── Discover DR podcasts via iTunes artist lookup ───────────────────────────
// DR's own Radio API requires a non-public key.
// Apple's iTunes lookup API is free, no key needed, and returns all 124+ DR
// podcasts with real RSS feed URLs (api.dr.dk/podcasts/v1/feeds/...).
// DR's iTunes artist ID is 204796829.
// iTunes allows cross-origin requests, so this works directly from the browser.

const DR_ITUNES_URL =
  'https://itunes.apple.com/lookup?id=204796829&entity=podcast&limit=200';

export async function discoverAndImportDRCatalogue(onProgress = null) {
  try {
    const res = await fetch(DR_ITUNES_URL);
    if (!res.ok) throw new Error(`iTunes API svarede ${res.status}`);
    const catalogue = await res.json();

    const items = (catalogue.results || []).filter(
      r => r.wrapperType === 'track' && r.feedUrl
    );

    if (items.length === 0) {
      return { success: false, message: 'Ingen DR podcasts fundet via iTunes.' };
    }

    let imported = 0;
    let updated = 0;
    let failed = 0;
    const details = [];

    for (const item of items) {
      const feedUrl = item.feedUrl;
      if (!feedUrl) continue;

      if (onProgress) onProgress({ done: imported + updated + failed, total: items.length });

      const iTunesCategory = item.primaryGenreName || '';
      const result = await importPublicServicePodcast(feedUrl, 'dr', iTunesCategory);
      if (!result.success) {
        failed++;
      } else if (result.isNew) {
        imported++;
      } else {
        updated++;
      }
      details.push({ feedUrl, ...result });
    }

    return { success: true, imported, updated, failed, details };
  } catch (err) {
    console.error('PublicServiceImportService.discoverAndImportDRCatalogue:', err);
    return { success: false, message: err.message };
  }
}

// ─── Internal: write episodes as subcollection ───────────────────────────────

async function _writeEpisodes(podcastId, items, podcastImage) {
  const writes = items.map((item, index) => {
    const episodeData = {
      title: item.title || 'Ukendt episode',
      description: item.description || '',
      pubDate: item.pubDate ? new Date(item.pubDate) : new Date(),
      link: item.link || '',
      guid: item.guid || `${podcastId}-ep-${index}`,
      audioUrl: item.enclosure?.link || '',
      duration: item.enclosure?.duration || '',
      image: item.thumbnail || podcastImage || '',
      episodeIndex: index,
      contentType: 'public_service',
    };
    return setDoc(doc(collection(db, `podcasts/${podcastId}/episodes`)), episodeData);
  });
  await Promise.all(writes);
}

export default {
  importPublicServicePodcast,
  syncPublicServicePodcast,
  syncAllPublicServicePodcasts,
  getPublicServicePodcasts,
  discoverAndImportDRCatalogue,
};
